import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Power, TrendingUp, TrendingDown, Zap, LogOut } from 'lucide-react';
import MainLayout from '../components/MainLayout';
import SpreadConfigForm from '../components/SpreadConfigForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { spreadConfigAPI, spreadSettingsAPI, SpreadSettingRow } from '../services/spreadApi';
import {
  SpreadConfig,
  SpreadMarket,
  SpreadDirection,
  SpreadOptionType,
  getLegOrder,
} from '../types/spread';

interface SpreadEntryExitPageProps {
  market: SpreadMarket;
}

const SpreadEntryExitPage: React.FC<SpreadEntryExitPageProps> = ({ market }) => {
  const accentColor = market === 'NIFTY' ? '#0d6efd' : '#fd7e14';

  const [settings, setSettings] = useState<SpreadSettingRow[]>([]);
  const bullishEnabled = !!settings.find(s => s.market === market && s.direction === 'BULLISH')?.is_enabled;
  const bearishEnabled = !!settings.find(s => s.market === market && s.direction === 'BEARISH')?.is_enabled;

  const [activeTab, setActiveTab] = useState<{ direction: SpreadDirection; optionType: SpreadOptionType } | null>(null);

  const [configs, setConfigs] = useState<SpreadConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SpreadConfig | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; config: SpreadConfig | null }>({ isOpen: false, config: null });
  const [instantModal, setInstantModal] = useState<{ isOpen: boolean; config: SpreadConfig | null; action: 'entry' | 'exit' }>({ isOpen: false, config: null, action: 'entry' });
  const [instantBusyId, setInstantBusyId] = useState<number | null>(null);
  const [instantMessage, setInstantMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await spreadConfigAPI.getConfigs(market);
      setConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch spread configs:', error);
    }
  }, [market]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await spreadSettingsAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch spread settings:', error);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchConfigs(), fetchSettings()]);
      setLoading(false);
    };
    load();
    const interval = setInterval(fetchConfigs, 5000);
    return () => clearInterval(interval);
  }, [fetchConfigs, fetchSettings]);

  const toggleDirectionEnabled = async (direction: SpreadDirection, nextEnabled: boolean) => {
    // Optimistic update, then persist
    setSettings(prev => {
      const exists = prev.find(s => s.market === market && s.direction === direction);
      if (exists) {
        return prev.map(s => (s.market === market && s.direction === direction ? { ...s, is_enabled: nextEnabled } : s));
      }
      return [...prev, { market, direction, is_enabled: nextEnabled, default_delay_seconds: 3 }];
    });
    try {
      await spreadSettingsAPI.updateSetting(market, direction, nextEnabled);
    } catch (error) {
      console.error('Failed to update spread master switch:', error);
      fetchSettings(); // revert on failure
    }
  };

  const openCreateForm = (direction: SpreadDirection, optionType: SpreadOptionType) => {
    setActiveTab({ direction, optionType });
    setEditingConfig(null);
    setShowForm(true);
  };

  const openEditForm = (cfg: SpreadConfig) => {
    setActiveTab({ direction: cfg.direction, optionType: cfg.option_type });
    setEditingConfig(cfg);
    setShowForm(true);
  };

  const handleSubmit = async (data: Partial<SpreadConfig>) => {
    try {
      if (editingConfig) {
        await spreadConfigAPI.updateConfig(market, editingConfig.id, data);
      } else {
        await spreadConfigAPI.createConfig(market, data);
      }
      await fetchConfigs();
      setShowForm(false);
      setEditingConfig(null);
    } catch (error) {
      console.error('Failed to save spread config:', error);
    }
  };

  const toggleActive = async (cfg: SpreadConfig) => {
    try {
      await spreadConfigAPI.toggleActive(market, cfg.id, !cfg.is_active);
      await fetchConfigs();
    } catch (error) {
      console.error('Failed to toggle spread config:', error);
    }
  };

  const confirmDelete = async () => {
    if (deleteModal.config) {
      try {
        await spreadConfigAPI.deleteConfig(market, deleteModal.config.id);
        await fetchConfigs();
      } catch (error) {
        console.error('Failed to delete spread config:', error);
      }
    }
    setDeleteModal({ isOpen: false, config: null });
  };

  const confirmInstantAction = async () => {
    const { config, action } = instantModal;
    setInstantModal({ isOpen: false, config: null, action: 'entry' });
    if (!config) return;
    setInstantBusyId(config.id);
    setInstantMessage(null);
    try {
      if (action === 'entry') {
        await spreadConfigAPI.instantEntry(market, config.id);
        setInstantMessage({ type: 'success', text: `Instant entry triggered for "${config.name}"` });
      } else {
        await spreadConfigAPI.instantExit(market, config.id);
        setInstantMessage({ type: 'success', text: `Instant exit triggered for "${config.name}"` });
      }
      await fetchConfigs();
    } catch (error: any) {
      setInstantMessage({ type: 'error', text: error.response?.data?.error || `Instant ${action} failed` });
    } finally {
      setInstantBusyId(null);
      setTimeout(() => setInstantMessage(null), 5000);
    }
  };

  const legTag = (cfg: SpreadConfig, which: 'first' | 'second') => {
    if (which === 'first') {
      return `ATM${cfg.first_leg_offset >= 0 ? '+' : ''}${cfg.first_leg_offset}`;
    }
    // Letter comes from which leg was placed first (B/S); sign comes from option type (CE=+, PE=-).
    const legOrder = getLegOrder(cfg.direction, cfg.option_type);
    const letter = legOrder === 'BUY_FIRST' ? 'B' : 'S';
    const sign = cfg.option_type === 'CE' ? '+' : '-';
    return `${letter}${sign}${cfg.second_leg_offset}`;
  };

  const renderDirectionPanel = (direction: SpreadDirection) => {
    const enabled = direction === 'BULLISH' ? bullishEnabled : bearishEnabled;
    const color = direction === 'BULLISH' ? '#10b981' : '#ef4444';
    const Icon = direction === 'BULLISH' ? TrendingUp : TrendingDown;

    return (
      <div style={{ border: `1px solid ${enabled ? color : '#e5e7eb'}`, borderRadius: '8px', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', background: enabled ? `${color}15` : '#f9fafb',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon size={18} color={color} />
            <span style={{ fontWeight: 700, fontSize: '14px', color }}>{direction}</span>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
            {enabled ? 'ENABLED' : 'DISABLED'}
            <div
              onClick={() => toggleDirectionEnabled(direction, !enabled)}
              style={{
                width: '38px', height: '20px', borderRadius: '10px', position: 'relative', cursor: 'pointer',
                background: enabled ? color : '#d1d5db', transition: 'background 0.2s',
              }}
            >
              <div style={{
                width: '16px', height: '16px', borderRadius: '50%', background: 'white', position: 'absolute',
                top: '2px', left: enabled ? '20px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </div>
          </label>
        </div>

        {enabled && (
          <div style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
              {(direction === 'BEARISH' ? ['PE', 'CE'] as SpreadOptionType[] : ['CE', 'PE'] as SpreadOptionType[]).map(opt => {
                const legOrder = getLegOrder(direction, opt);
                const isActiveTab = activeTab?.direction === direction && activeTab?.optionType === opt;
                return (
                  <button
                    key={opt}
                    onClick={() => setActiveTab({ direction, optionType: opt })}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: '6px', cursor: 'pointer',
                      border: `1px solid ${isActiveTab ? color : '#d1d5db'}`,
                      background: isActiveTab ? color : 'white',
                      color: isActiveTab ? 'white' : '#374151',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '13px' }}>{opt} — {legOrder === 'BUY_FIRST' ? 'Buy → Sell' : 'Sell → Buy'}</div>
                    <div style={{ fontSize: '11px', opacity: 0.85 }}>
                      {legOrder === 'BUY_FIRST' ? 'Debit spread (buy first, sell further OTM after delay)' : 'Credit spread (sell first, buy further OTM as hedge after delay)'}
                    </div>
                  </button>
                );
              })}
            </div>

            {activeTab && activeTab.direction === direction && (
              <button
                onClick={() => openCreateForm(direction, activeTab.optionType)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                  border: `1px dashed ${color}`, borderRadius: '6px', background: 'white', color,
                  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                }}
              >
                <Plus size={14} /> Create Config ({activeTab.optionType})
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Configs list grouped: all BULLISH first (CE then PE within), then BEARISH (CE then PE
  // within), with a divider row between the two direction groups.
  const marketConfigs = [...configs.filter(c => c.market === market)].sort((a, b) => {
    if (a.direction !== b.direction) return a.direction === 'BULLISH' ? -1 : 1;
    if (a.option_type !== b.option_type) return a.option_type === 'CE' ? -1 : 1;
    return 0;
  });

  return (
    <MainLayout>
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '18px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: accentColor, margin: 0 }}>
            {market === 'NIFTY' ? 'NY SPREAD' : 'SX SPREAD'} — Entry / Exit
          </h2>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            Two-leg (Buy + Sell) spread strategy for {market === 'NIFTY' ? 'NIFTY' : 'SENSEX'}.
            Existing single-leg {market === 'NIFTY' ? 'NSE' : 'BSE'} Entry/Exit is unaffected by this page.
          </div>
        </div>

        {renderDirectionPanel('BULLISH')}
        {renderDirectionPanel('BEARISH')}

        {/* Config list */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>Configs</h3>
          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  {['Conf Name', 'Direction', 'Type', 'Buy', 'Sell', 'Delay(s)', '% Cap', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#374151' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {marketConfigs.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
                      No configs yet — enable Bullish/Bearish above and create one.
                    </td>
                  </tr>
                ) : marketConfigs.map((cfg, idx) => {
                  const legOrder = getLegOrder(cfg.direction, cfg.option_type);
                  const buyTag = legOrder === 'BUY_FIRST' ? legTag(cfg, 'first') : legTag(cfg, 'second');
                  const sellTag = legOrder === 'BUY_FIRST' ? legTag(cfg, 'second') : legTag(cfg, 'first');
                  const showDivider = idx > 0 && marketConfigs[idx - 1].direction !== cfg.direction;
                  return (
                    <React.Fragment key={cfg.id}>
                    {showDivider && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, borderBottom: '2px solid #d1d5db' }} />
                      </tr>
                    )}
                    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px 10px' }}>{cfg.name}</td>
                      <td style={{ padding: '8px 10px', color: cfg.direction === 'BULLISH' ? '#10b981' : '#ef4444', fontWeight: 600 }}>{cfg.direction}</td>
                      <td style={{ padding: '8px 10px' }}>{cfg.option_type}</td>
                      <td style={{ padding: '8px 10px' }}>{buyTag}</td>
                      <td style={{ padding: '8px 10px' }}>{sellTag}</td>
                      <td style={{ padding: '8px 10px' }}>{cfg.delay_seconds}</td>
                      <td style={{ padding: '8px 10px' }}>{cfg.capital_percentage}%</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '11px',
                          background: cfg.is_active ? '#d1fae5' : '#f3f4f6',
                          color: cfg.is_active ? '#065f46' : '#6b7280',
                        }}>
                          {cfg.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <button onClick={() => openEditForm(cfg)} title="Edit" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#3b82f6', padding: '6px', borderRadius: '4px' }}><Edit size={14} /></button>
                          <button onClick={() => toggleActive(cfg)} title="Toggle Active" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: cfg.is_active ? '#10b981' : '#dc2626', padding: '6px', borderRadius: '4px' }}><Power size={14} /></button>
                          <button onClick={() => setDeleteModal({ isOpen: true, config: cfg })} title="Delete" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#ef4444', padding: '6px', borderRadius: '4px' }}><Trash2 size={14} /></button>
                          <span style={{ width: '1px', height: '16px', background: '#e5e7eb', margin: '0 4px' }} />
                          <button
                            onClick={() => setInstantModal({ isOpen: true, config: cfg, action: 'entry' })}
                            disabled={instantBusyId === cfg.id}
                            title="Instant Entry (fires now, ignores X/Y signal)"
                            style={{ border: '1px solid #10b981', borderRadius: '4px', background: 'white', cursor: instantBusyId === cfg.id ? 'wait' : 'pointer', color: '#10b981', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, opacity: instantBusyId === cfg.id ? 0.5 : 1 }}
                          >
                            <Zap size={12} /> Entry
                          </button>
                          <button
                            onClick={() => setInstantModal({ isOpen: true, config: cfg, action: 'exit' })}
                            disabled={instantBusyId === cfg.id}
                            title="Instant Exit (fires now, ignores SL/Target/TSL)"
                            style={{ border: '1px solid #ef4444', borderRadius: '4px', background: 'white', cursor: instantBusyId === cfg.id ? 'wait' : 'pointer', color: '#ef4444', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, opacity: instantBusyId === cfg.id ? 0.5 : 1 }}
                          >
                            <LogOut size={12} /> Exit
                          </button>
                        </div>
                      </td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {activeTab && (
        <SpreadConfigForm
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
          config={editingConfig}
          market={market}
          direction={activeTab.direction}
          optionType={activeTab.optionType}
        />
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, config: null })}
        onConfirm={confirmDelete}
        title="Delete Config"
        message={`Delete config "${deleteModal.config?.name}"? This cannot be undone.`}
        type="danger"
      />

      <ConfirmationModal
        isOpen={instantModal.isOpen}
        onClose={() => setInstantModal({ isOpen: false, config: null, action: 'entry' })}
        onConfirm={confirmInstantAction}
        title={instantModal.action === 'entry' ? 'Instant Entry — Real Order' : 'Instant Exit — Real Order'}
        message={
          instantModal.action === 'entry'
            ? `This will place a REAL Buy-then-Sell order sequence for "${instantModal.config?.name}" RIGHT NOW, using its configured expiry and lot size — the X/Y signal wait is skipped. Continue?`
            : `This will close whatever legs are currently open for "${instantModal.config?.name}" RIGHT NOW — the SL/Target/TSL check is skipped. Continue?`
        }
        confirmText={instantModal.action === 'entry' ? 'Fire Entry Now' : 'Fire Exit Now'}
        type="danger"
      />

      {instantMessage && (
        <div style={{
          position: 'fixed', bottom: '20px', right: '20px', zIndex: 3000,
          padding: '10px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
          background: instantMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: instantMessage.type === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${instantMessage.type === 'success' ? '#10b981' : '#ef4444'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {instantMessage.text}
        </div>
      )}
    </MainLayout>
  );
};

export default SpreadEntryExitPage;
